import { CustomTable } from '@/components/Table';
import { Button, InputText, SelectCustom } from '@/components';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventMachineSaleProductReport } from '@/model/EventMachineSaleProductReport';
import React, { Fragment } from 'react';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { ReportDropdownHeader } from '../../ReportDropdownHeader';
import { SaleProductSummaryCard } from '../../SaleProductSummaryCard';

interface StateProps {
  machines: EventMachineSaleProductReport[];
  allMachines?: EventMachineSaleProductReport[];
  serialNumber: string;
  productName: string;
  posOptions: { label: string; value: string }[];
  onChangeSerialNumber: (value: string) => void;
  onChangeProductName: (value: string) => void;
  onFilter: () => void;
  onClearFilter: () => void;
  onExportPDF?: (machinesToExport?: EventMachineSaleProductReport[]) => void;
}

export const SaleProductMachineContent: React.FC<StateProps> = ({
  machines,
  allMachines = [],
  serialNumber,
  productName,
  posOptions,
  onChangeSerialNumber,
  onChangeProductName,
  onFilter,
  onClearFilter,
  onExportPDF,
}): JSX.Element => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      onFilter();
    }
  };

  const displayedMachines = machines.filter(m => {
    if (serialNumber.trim()) {
      const matchSerial = m.serialNumber?.toLowerCase().includes(serialNumber.trim().toLowerCase());
      if (!matchSerial) return false;
    }
    return true;
  });

  const isFiltered = Boolean(serialNumber.trim() || productName.trim());

  return (
    <Fragment>
      {/* Container de Filtros com Flexbox e Botões alinhados à direita na linha abaixo */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
        }}
      >
        <h6
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#495057',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '16px',
          }}
        >
          Filtros de Pesquisa
        </h6>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 250px' }}>
              <SelectCustom
                name="filterSerialNumber"
                label="Filtrar por Serial da Máquina"
                placeholder="Digite ou selecione a POS"
                value={serialNumber}
                onChange={(option: any) => onChangeSerialNumber(option?.value || '')}
                options={posOptions}
                isCreatable
                isClearable
              />
            </div>
            <div style={{ flex: '1 1 250px' }}>
              <InputText
                name="filterProductName"
                label="Filtrar por Produto"
                placeholder="Digite o nome do produto"
                value={productName}
                onChange={e => onChangeProductName(e.target.value)}
                keyDow={handleKeyDown}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button title="Pesquisar" theme="blue" onClick={onFilter} />
            <Button title="Limpar" theme="red" onClick={onClearFilter} />
            {onExportPDF && (
              <Button
                title="Exportar PDF"
                theme="outlineDark"
                onClick={() => onExportPDF && onExportPDF(displayedMachines)}
              />
            )}
          </div>
        </div>
      </div>

      {isFiltered && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '10px 14px',
            backgroundColor: '#e8f4fd',
            borderRadius: '8px',
            border: '1px solid #b6d4fe',
            color: '#1b4880',
            fontSize: '13px',
          }}
        >
          <strong>Filtros ativos ({displayedMachines.length} encontradas):</strong>
          <span>
            {[
              serialNumber.trim() ? `Serial: ${serialNumber}` : null,
              productName.trim() ? `Produto: ${productName}` : null,
            ]
              .filter(Boolean)
              .join(' | ')}
          </span>
        </div>
      )}

      <SaleProductSummaryCard
        title="Resumo das Maquininhas POS"
        items={displayedMachines}
        totalItemsCount={allMachines.length > 0 ? allMachines.length : machines.length}
        unitName={{ singular: 'maquininha POS', plural: 'maquininhas POS' }}
        isFiltered={isFiltered}
      />

      {displayedMachines.length === 0 && (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '40px 24px',
            textAlign: 'center',
            border: '1px solid #e9ecef',
            color: '#6c757d',
            marginBottom: '24px',
          }}
        >
          Nenhuma maquininha POS encontrada para os filtros selecionados.
        </div>
      )}

      {displayedMachines.map(data => (
        <div key={data.id} className="mb-3">
          <SuperCollapse
            title={
              <ReportDropdownHeader
                title={`POS: ${data.serialNumber}`}
                summary={data.summary}
                payments={data.payments}
              />
            }
            content={
              <div className="p-3 bg-white rounded">
                {data.categories.map(category => (
                  <div key={category.id} className="mb-4">
                    <h6 className="font-weight-bold mb-2 text-secondary">{category.name}</h6>
                    <CustomTable
                      theme="secondaryWithoutBorder"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Produto',
                          selector: row => row.productName,
                        },
                        {
                          name: 'Vendidos',
                          selector: row => row.productSold,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.unitValue,
                        },
                        {
                          name: 'Venda',
                          selector: row => row.totalSaleValue,
                        },
                        {
                          name: 'Comissão',
                          selector: row => row.totalCommission,
                        },
                        {
                          name: 'Venda + Comissão',
                          selector: row => row.totalSaleWithCommission,
                        },
                      ]}
                      data={
                        category.products.map(product => ({
                          productName: product.name,
                          productSold: product.amountSold,
                          unitValue: toCurrency(product.unitValue),
                          totalSaleValue: toCurrency(product.totalSaleValue),
                          totalCommission: toCurrency(product.totalCommission),
                          totalSaleWithCommission: toCurrency(product.totalSaleWithCommission),
                        })) || []
                      }
                    />
                  </div>
                ))}
              </div>
            }
          />
        </div>
      ))}
    </Fragment>
  );
};
